import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import { initWeekdays } from 'src/components/SideBar/CalendarSideBar/components/CreateScheduleMenu/components/Popup/components';
import { UserRoles } from 'src/constants/user';

import { getNormalizedWorkingTimes } from '../calendar';

import { IPeriod, IPeriodTime, PriceActionTypes } from './helpers.types';

interface elementStyleProp {
  role: string;
  roleType: Array<string> | string;
  firstOption: string;
  lastOption: string;
}

export const elementStyle = ({ roleType, role, firstOption, lastOption }: any) => {
  if (Array.isArray(roleType)) {
    const [firstRole, secondRole] = roleType;

    return `${
      // @ts-ignore
      role === UserRoles[firstRole] || role === UserRoles[secondRole] ? `${firstOption}` : `${lastOption}`
    }`;
  } else if (typeof roleType === 'string' && role) {
    return `${
      // @ts-ignore
      role === UserRoles[`${roleType}`] ? `${firstOption}` : `${lastOption}`
    }`;
  }
};

const isDateValid = (employeePeriod: any, studioPeriod: any) => {
  const { from: employeeDateFrom, to: employeeDateTo } = employeePeriod;
  const { from: studioDateFrom, to: studioDateTo } = studioPeriod;

  if (
    dayjs(employeeDateTo).isAfter(dayjs(studioDateTo).hour(0)) ||
    dayjs(employeeDateFrom).isBefore(dayjs(studioDateFrom).hour(0))
  ) {
    return false;
  }

  return true;
};

const isTimeValid = (employeePeriodTime: any, studioPeriodTime: any) => {
  const { from: startEmployeeTime, to: endEmployeeTime } = getStartAndEndTime(<IPeriodTime>employeePeriodTime);
  const { from: startStudioTime, to: endStudioTime } = getStartAndEndTime(<IPeriodTime>studioPeriodTime);

  const isStartTimeInvalid = startEmployeeTime.isBefore(startStudioTime) || startEmployeeTime.isAfter(endStudioTime);
  const isEndTimeInvalid = endEmployeeTime.isBefore(startStudioTime) || endEmployeeTime.isAfter(endStudioTime);
  const isStartAndEndTimeTheSame = startEmployeeTime.isSame(endEmployeeTime, 'minutes');

  const isValid = !(isStartTimeInvalid || isEndTimeInvalid || isStartAndEndTimeTheSame);

  return isValid;
};

const getTime = (time: IPeriod) => {
  return dayjs().set('hours', time.hours).set('minutes', time.minutes).set('seconds', 0);
}

const getStartAndEndTime = (periodTime: IPeriodTime): { from: Dayjs, to: Dayjs } => {
  return {
    from: getTime(periodTime.from),
    to: getTime(periodTime.to),
  }
};

export const validateWorkingTimes = (employeeArray: any, studiosArray: any) => {
  const comparisonResult = employeeArray.map((employee: any, index: number) => {
    const studio = studiosArray?.[index];
    const employeeSchedule = employee?.schedule?.workingTimes?.[0];
    const studioSchedule = studio?.schedule?.workingTimes?.[0];

    const weekdaysValidArray = Object.entries(employeeSchedule?.daysPeriods)
      ?.map(([key, employeePeriodTime]: any) => {
        const studioPeriodTime = studioSchedule?.daysPeriods?.[key];

        const isValid = isTimeValid(employeePeriodTime, studioPeriodTime);

        return {
          weekday: key,
          isValid,
        };
      });

    const isPeriodDateValid = isDateValid(employeeSchedule?.period, studioSchedule?.period);

    return {
      id: studio?.id,
      title: studio?.title,
      isPeriodDateValid,
      weekdaysValidArray,
    };
  });

  return comparisonResult;
};

export const gettingWorkingTimes = (weekdays: any) => {
  if (weekdays != null) {
    const undisabledWeekdayKeys = Object.keys(weekdays);

    const disableWeekdays = initWeekdays.filter((weekday: any) => !undisabledWeekdayKeys.includes(weekday.key));
    return disableWeekdays;
  }

  return [];
};

export const getNormalizedWorkplaces = (studiosTabsArray: any, studiosTabsRefs: any) => {
  // @ts-ignore
  const normalizedWorkplaces = studiosTabsArray.map(({ id }, index) => {
    // @ts-ignore
    const { workingTimesRef, rulesRef } = studiosTabsRefs?.current?.[index] || {};

    const normalizedWorkingTimes = getNormalizedWorkingTimes([
      {
        // @ts-ignore
        daysPeriods: workingTimesRef?.current?.getDaysPeriods(),
        // @ts-ignore
        period: rulesRef?.current?.getPeriod(),
      },
    ]);

    return {
      studioId: id,
      schedule: {
        workingTimes: normalizedWorkingTimes,
      },
    };
  });

  return normalizedWorkplaces;
};

export const priceNormalizer = (price: any, action: PriceActionTypes) => {
  if (PriceActionTypes.Showing === action) {
    return price / 100;
  } else if (PriceActionTypes.Sending === action) {
    return price * 100;
  } else return 0;
};

export const getArrayForBookingSingleSelector = (service: any) => {
  return service.map(({ id, title }: any) => ({
    id,
    label: title,
  }));
};

export const getCurrentPath = (endpointId: number | null, toCurrentValuePath: any) => {
  const result = { cat: null, subcat: null, service: null };

  if (toCurrentValuePath && toCurrentValuePath.services) {
    const service = toCurrentValuePath.services.find((service: { id: any; }) => service.id === endpointId);
    if (service) {
      result.cat = toCurrentValuePath.id;
      result.service = service.id;
      return result;
    }
  }

  if (toCurrentValuePath && toCurrentValuePath.children && toCurrentValuePath.children.length > 0) {
    for (const child of toCurrentValuePath.children) {
      const temp: any = getCurrentPath(endpointId, child);
      if (temp) {
        result.cat = toCurrentValuePath.id;
        result.subcat = temp.cat;
        result.service = temp.service;
        return result;
      }
    }
  }
  return result;
}

export const getCategoryFromService = (
  service: { id: number | string, title: string, categories?: Array<any>, },
  categories: Array<any>,
) => {
  const serviceParent = service.categories && service.categories.length > 0 ?
    service.categories.reduce((max, current) => current.id > max.id ? current : max) : null;

  const result = {
    service: {id: +service.id, title: service.title},
    cat: {id: 0, title: ''},
    subcat: {id: 0, title: ''}
  };



  if (serviceParent !== null) {
    for (const category of categories) {
      if (category && +category.id === +serviceParent.id) {
        result.cat.id = +category.id;
        result.cat.title = category.title;
        return result;
      }

      if (category.children) {
        for (const child of category.children) {
          if (child && +child.id === +serviceParent.id) {
            result.cat.id = +category.id;
            result.cat.title = category.title;
            result.subcat.id = +child.id;
            result.subcat.title = child.title;
            return result;
          }
        }
      }
    }
  }

  return result;
};

export const getServicesCountFromCategory = (category: any) => {
  const servicesCount = (category?.services?.length || 0) + category?.children?.reduce((a: number, b: any) => a + b.services.length, 0);
  return servicesCount;
}

export const getAvailableStudioTemplates = (categoryTemplates: any[], targets: any[]) => {

  const filterServices = (services: any[]) => services.filter(service => targets.includes(service.id));

  const filterRecursive = (category: any) => {
    const filteredServices = filterServices(category.services);
    let filteredChildren = [];

    if (category.children) {
      filteredChildren = category.children.map(
        (child: any) => filterRecursive(child)).filter((child: any) => child.services.length > 0);
    }
    return {
      ...category,
      services: filteredServices,
      children: filteredChildren
    };
  };

  const filteredCategories = categoryTemplates.map((category: any) => filterRecursive(category))
    .filter((category: any) => category.services.length > 0 || category.children.length > 0);

  return filteredCategories;
};

export const filterCategoriesData = (mySearchBy: any, input: string, value: { categories: any[] }) => {

  if (input.trim() === "") {
    return value.categories
  }

  const ensureArray = (data: any) => Array.isArray(data) ? data : [];

  const filterServices = (categories: any[], includeChildren = false) => {
    if (!Array.isArray(categories)) return [];

    return categories.reduce((acc, category) => {
      const servicesMatch = ensureArray(category.services).filter(service =>
        service.title.toLowerCase().includes(input.toLowerCase())
      );

      const childrenMatches = includeChildren && category.children ?
        filterServices(ensureArray(category.children), false) :
        [];

      if (servicesMatch.length > 0 || childrenMatches.length > 0) {
        acc.push({
          ...category,
          services: servicesMatch,
          children: includeChildren ? childrenMatches : ensureArray(category.children)
        });
      }

      return acc;
    }, []);
  };

  switch (mySearchBy) {
    case 'Service':
      return filterServices(value.categories, true);
    case 'Category':
      return value.categories.filter(category =>
        category.title.toLowerCase().includes(input.toLowerCase())
      );
    case 'Subcategory':
      return value.categories
        .map(category => ({
          ...category,
          children: category.children?.filter((child: { title: string }) =>
            child.title.toLowerCase().includes(input.toLowerCase())
          )
        }))
        .filter(category => category.children && category.children.length > 0)
        .map(category => ({ ...category, children: category.children }));
    default:
      return [];
  }
};

export const sortCategoriesData = (services: any[], sortBy: string) => {

  const sortServicesInObject = (obj: any) => {
    const newObj = _.cloneDeep(obj);

    if (newObj.children && newObj.children.length > 0) {
      newObj.children = newObj.children.map(sortServicesInObject);
    }
    if (newObj.services && newObj.services.length > 0) {
      newObj.services = newObj.services.sort((a: { id: number; duration: number; title: string; }, b: { id: number; duration: number; title: any; }) => {
        if (sortBy.toLowerCase() === 'id') {
          return a.id - b.id;
        } else if (sortBy.toLowerCase() === 'duration') {
          return a.duration - b.duration;
        } else if (sortBy.toLowerCase() === 'name') {
          return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
        } else {
          return a.id - b.id;
        }
      });
    }
    return newObj;
  };

  return services.map(sortServicesInObject);
};


export const countCheckedServices = (objects: any) => {
  let count = 0;
  function recursiveCount (objects: any) {
    objects.forEach((obj: { services: { isChecked: any; }[]; children: any; }) => {
      // Count isChecked services in the current object
      if (obj.services && Array.isArray(obj.services)) {
        obj.services.forEach((service: { isChecked: any; }) => {
          if (service.isChecked) {
            // eslint-disable-next-line no-plusplus
            count++;
          }
        });
      }
      if (obj.children && Array.isArray(obj.children)) {
        recursiveCount(obj.children);
      }
    });
  }
  recursiveCount(objects);
  return count;
}
